/**
 * Central shared-state store for the MCT dashboard.
 *
 * See docs/ARCHITECTURE.md §6 and docs/ADRs/012-shared-state-architecture.md.
 *
 * Each domain (portfolio / positions / orders / trades / signals) is a slice
 * of this store. Components consume via the hooks in `hooks/use*State.ts`.
 *
 * Hydration:
 *   1. WS `state_init` for each domain → fully replaces snapshot + sets seq
 *   2. WS `state_delta` → JSON-merge-patch into snapshot, increments seq
 *   3. Sequence gap detected → store calls REST `/api/state/{key}` for resync
 *   4. WS disconnect → fallback polling via stateChannel.ts
 *
 * Feature flag: `localStorage.mct_state_store_enabled === "true"` opts an
 * individual component into reading from this store. When false, components
 * use their legacy `useQuery` paths. Lets us migrate one component at a time.
 */
import { create } from "zustand";

export type DomainKey =
  | "portfolio"
  | "positions"
  | "orders"
  | "trades"
  | "signals"
  // Phase A.1+ — frontend-refactor-state-everywhere
  | "indices"
  | "system_health"
  | "auth_status"
  | "alerts"
  | "settings_meta"
  | "gtt"
  | "scheduler_status"
  | "metrics"
  | "feature_flags"
  | "dispatcher"
  // 2026-04-27 — replaces 30s /holdings + 60s /charges polls
  | "holdings"
  | "charges"
  // 2026-04-27 Phase H — bundles 4 status polls into one push
  | "system_toggles"
  // 2026-04-27 Phase I — bundles pnl-by-index + lifecycles + bn_directional
  | "system_extras";

export interface DomainSlice<T = Record<string, unknown>> {
  snapshot: T | null;
  lastSeq: number;
  isStale: boolean;       // true while WS disconnected + waiting for reconnect/resync
  lastUpdatedAt: number;  // Date.now() of last apply (for stale-detection in UI)
}

interface StateStore {
  portfolio: DomainSlice;
  positions: DomainSlice;
  orders:    DomainSlice;
  trades:    DomainSlice;
  signals:           DomainSlice;
  indices:           DomainSlice;
  system_health:     DomainSlice;
  auth_status:       DomainSlice;
  alerts:            DomainSlice;
  settings_meta:     DomainSlice;
  gtt:               DomainSlice;
  scheduler_status:  DomainSlice;
  metrics:           DomainSlice;
  feature_flags:     DomainSlice;
  dispatcher:        DomainSlice;
  holdings:          DomainSlice;
  charges:           DomainSlice;
  system_toggles:    DomainSlice;
  system_extras:     DomainSlice;

  /**
   * Apply a `state_init` (full snapshot replacement).
   * Resets the slice to the new snapshot + seq.
   */
  applyInit: (key: DomainKey, data: Record<string, unknown>, seq: number) => void;

  /**
   * Apply a `state_delta` (JSON-merge-patch). If `seq != lastSeq + 1` then
   * the caller is responsible for triggering a resync via REST. This method
   * still applies the delta best-effort (it'll be corrected on the resync).
   */
  applyDelta: (key: DomainKey, delta: Record<string, unknown>, seq: number) => boolean;

  /**
   * Mark all slices as stale (WS disconnected). Cleared on next applyInit.
   */
  markStale: () => void;
}

const _emptySlice = (): DomainSlice => ({
  snapshot: null,
  lastSeq: 0,
  isStale: true,
  lastUpdatedAt: 0,
});

/** RFC 7396 merge-patch (subset). Mutates target in place. */
function mergeInto(target: Record<string, unknown>, patch: Record<string, unknown>): void {
  for (const [k, v] of Object.entries(patch)) {
    if (v === null) {
      delete target[k];
    } else if (
      typeof v === "object" && !Array.isArray(v) &&
      typeof target[k] === "object" && target[k] !== null && !Array.isArray(target[k])
    ) {
      mergeInto(target[k] as Record<string, unknown>, v as Record<string, unknown>);
    } else {
      target[k] = v;
    }
  }
}

export const useStateStore = create<StateStore>((set) => ({
  portfolio: _emptySlice(),
  positions: _emptySlice(),
  orders:    _emptySlice(),
  trades:    _emptySlice(),
  signals:          _emptySlice(),
  indices:          _emptySlice(),
  system_health:    _emptySlice(),
  auth_status:      _emptySlice(),
  alerts:           _emptySlice(),
  settings_meta:    _emptySlice(),
  gtt:              _emptySlice(),
  scheduler_status: _emptySlice(),
  metrics:          _emptySlice(),
  feature_flags:    _emptySlice(),
  dispatcher:       _emptySlice(),
  holdings:         _emptySlice(),
  charges:          _emptySlice(),
  system_toggles:   _emptySlice(),
  system_extras:    _emptySlice(),

  applyInit: (key, data, seq) => set(() => ({
    [key]: {
      snapshot:      data,
      lastSeq:       seq,
      isStale:       false,
      lastUpdatedAt: Date.now(),
    },
  } as Partial<StateStore>)),

  applyDelta: (key, delta, seq) => {
    let gap = false;
    set((s) => {
      const slice = s[key];
      if (slice.snapshot === null) {
        // No initial snapshot yet — caller should resync. Best-effort apply.
        gap = true;
        return {
          [key]: {
            snapshot:      { ...delta },
            lastSeq:       seq,
            isStale:       true,
            lastUpdatedAt: Date.now(),
          },
        } as Partial<StateStore>;
      }
      if (seq !== slice.lastSeq + 1) {
        gap = true;  // signal caller to resync
      }
      const next = { ...(slice.snapshot as Record<string, unknown>) };
      mergeInto(next, delta);
      return {
        [key]: {
          snapshot:      next,
          lastSeq:       seq,
          isStale:       gap,
          lastUpdatedAt: Date.now(),
        },
      } as Partial<StateStore>;
    });
    return !gap;
  },

  // 2026-04-28 (audit re-run): added 4 newer domains (holdings, charges,
  // system_toggles, system_extras). Without them in markStale, on WS
  // disconnect these stayed !isStale forever — UI showed 60s-old data
  // until next periodic refresh, no resync triggered.
  markStale: () => set((s) => ({
    portfolio: { ...s.portfolio, isStale: true },
    positions: { ...s.positions, isStale: true },
    orders:    { ...s.orders,    isStale: true },
    trades:    { ...s.trades,    isStale: true },
    signals:          { ...s.signals,          isStale: true },
    indices:          { ...s.indices,          isStale: true },
    system_health:    { ...s.system_health,    isStale: true },
    auth_status:      { ...s.auth_status,      isStale: true },
    alerts:           { ...s.alerts,           isStale: true },
    settings_meta:    { ...s.settings_meta,    isStale: true },
    gtt:              { ...s.gtt,              isStale: true },
    scheduler_status: { ...s.scheduler_status, isStale: true },
    metrics:          { ...s.metrics,          isStale: true },
    feature_flags:    { ...s.feature_flags,    isStale: true },
    dispatcher:       { ...s.dispatcher,       isStale: true },
    holdings:         { ...s.holdings,         isStale: true },
    charges:          { ...s.charges,          isStale: true },
    system_toggles:   { ...s.system_toggles,   isStale: true },
    system_extras:    { ...s.system_extras,    isStale: true },
  })),
}));

/** Per-component opt-in to reading from the new store.
 *
 * 2026-04-24: default flipped to ON. The state store + WS state-diff path
 * has been validated in paper mode for a session and the legacy useQuery
 * polls of /api/portfolio/, /api/orders/, /api/paper/positions etc. were
 * the dominant source of DB load during scanner bursts. Setting the flag
 * to "false" explicitly opts back into the legacy path; an unset key now
 * means "use the new store".
 *
 * Migrated components (read from store instead of useQuery when enabled):
 *   SignalsTable, PaperTradingPanel, ActivePositions, Positions,
 *   PaperTradingPage, Dashboard top stat row.
 */
export function isStateStoreEnabled(): boolean {
  try {
    const v = localStorage.getItem("mct_state_store_enabled");
    if (v === "false") return false;   // explicit opt-out
    return true;                        // default on (was: v === "true")
  } catch {
    return true;                        // localStorage unavailable → default on
  }
}
